// ============================================
// COMMUNITY HUB — CUSTOM HOOKS
// ============================================

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { communityApi } from './api';
import { tokenManager } from './api';
import type {
  User,
  Community,
  Post,
  Comment,
  PermissionMap,
  SocialPlatform,
  ToastNotification,
} from './types';
import {
  PRO_SOCIAL_PLATFORMS,
  ENTERPRISE_SOCIAL_PLATFORMS,
  PLATFORM_COMMISSION_RATE,
} from './types';

// ─── USE AUTH ──────────────────────────────
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = tokenManager.getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userData = payload.user || payload;
        const plan = (userData.plan || 'starter').toLowerCase();

        // Plan-based defaults (hardcoded fallback)
        const planConfig: Record<string, { maxCommunities: number; communitiesOwned: number }> = {
          starter: { maxCommunities: 0, communitiesOwned: 0 },
          pro: { maxCommunities: 4, communitiesOwned: userData.communitiesOwned || 0 },
          professional: { maxCommunities: 4, communitiesOwned: userData.communitiesOwned || 0 },
          enterprise: { maxCommunities: 999, communitiesOwned: userData.communitiesOwned || 0 },
        };

        const config = planConfig[plan] || planConfig.starter;

        setUser({
          _id: userData.id || userData._id,
          name: userData.name || 'User',
          email: userData.email || '',
          plan: plan,
          communitiesOwned: config.communitiesOwned,
          maxCommunities: config.maxCommunities,
        });
      } catch {
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const getCurrentUserId = useCallback((): string | null => {
    try {
      const token = tokenManager.getToken();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || payload._id || payload.user?.id || payload.user?._id || null;
    } catch {
      return null;
    }
  }, []);

  const isOwner = useCallback(
    (communityOwnerId: string): boolean => {
      const userId = getCurrentUserId();
      return userId === communityOwnerId;
    },
    [getCurrentUserId]
  );

  return { user, loading, getCurrentUserId, isOwner };
}

// ─── USE PERMISSIONS ───────────────────────
export function usePermissions(user: User | null): PermissionMap {
  return useMemo(() => {
    const plan = user?.plan || 'starter';

    // Plan-based limits
    const planLimits: Record<string, number> = {
      starter: 0,
      pro: 4,
      professional: 4,
      enterprise: Infinity,
    };

    const maxCommunities = planLimits[plan] ?? 0;
    const communitiesOwned = user?.communitiesOwned || 0;
    const communitiesRemaining =
      plan === 'enterprise'
        ? Infinity
        : Math.max(0, maxCommunities - communitiesOwned);

    const canCreateCommunity =
      plan === 'enterprise' ||
      (plan === 'pro' && communitiesOwned < maxCommunities);

    return {
      canView: true,
      canJoin: plan !== 'starter',
      canPost: plan !== 'starter',
      canComment: plan !== 'starter',
      canLike: plan !== 'starter',
      canVote: plan !== 'starter',
      canRSVP: plan !== 'starter',
      canCreateCommunity,
      canMonetize: plan !== 'starter',
      canAccessAnalytics: plan === 'enterprise',
      canExportMembers: plan === 'enterprise',
      canCustomBrand: plan === 'enterprise',
      canAccessAPI: plan === 'enterprise',
      allowedSocialPlatforms:
        plan === 'enterprise'
          ? ENTERPRISE_SOCIAL_PLATFORMS
          : plan === 'pro' || plan === 'professional'
          ? PRO_SOCIAL_PLATFORMS
          : [],
      maxCommunities: plan === 'enterprise' ? 999 : maxCommunities,
      communitiesRemaining: plan === 'enterprise' ? 999 : communitiesRemaining,
    };
  }, [user]);
}

// ─── USE COMMUNITIES ───────────────────────
export function useCommunities() {
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const abortRef = useRef<AbortController | null>(null);

  const fetchCommunities = useCallback(
    async (
      params: {
        search?: string;
        category?: string;
        filter?: string;
        reset?: boolean;
      } = {}
    ) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      if (params.reset) {
        setPage(1);
        setCommunities([]);
      }

      setLoading(true);
      setError(null);

      try {
        const currentPage = params.reset ? 1 : page;
        const res = await communityApi.getCommunities({
          search: params.search,
          category: params.category,
          filter: params.filter,
          page: currentPage,
          limit: 12,
        });

        if (res.success && res.data) {
          const newCommunities = res.data.communities || [];
          setCommunities((prev) =>
            params.reset ? newCommunities : [...prev, ...newCommunities]
          );
          setHasMore(res.data.pagination?.hasMore ?? false);
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load communities');
        }
      } finally {
        setLoading(false);
      }
    },
    [page]
  );

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [loading, hasMore]);

  useEffect(() => {
    if (page > 1) {
      fetchCommunities();
    }
  }, [page]);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  return { communities, loading, error, hasMore, fetchCommunities, loadMore };
}

// ─── USE COMMUNITY DETAIL ──────────────────
export function useCommunityDetail(communityId: string | null) {
  const [community, setCommunity] = useState<Community | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await communityApi.getCommunity(communityId);
      if (res.success && res.data) {
        setCommunity(res.data.community);
        setPosts(res.data.posts || []);
        setIsMember(res.data.isMember);
        setIsAdmin(res.data.isAdmin);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load community');
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const addPost = useCallback((post: Post) => {
    setPosts((prev) => [post, ...prev]);
  }, []);

  const updatePost = useCallback((postId: string, updates: Partial<Post>) => {
    setPosts((prev) =>
      prev.map((p) => (p._id === postId ? { ...p, ...updates } : p))
    );
  }, []);

  const removePost = useCallback((postId: string) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  }, []);

  const updateCommunity = useCallback((updates: Partial<Community>) => {
    setCommunity((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  return {
    community,
    posts,
    loading,
    error,
    isMember,
    isAdmin,
    fetchDetail,
    addPost,
    updatePost,
    removePost,
    updateCommunity,
    setIsMember,
  };
}

// ─── USE TOAST NOTIFICATIONS ───────────────
export function useToast() {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback(
    (type: ToastNotification['type'], message: string, duration = 4000) => {
      const id = String(++toastIdRef.current);
      const toast: ToastNotification = { id, type, message, duration };
      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }

      return id;
    },
    []
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback((msg: string) => addToast('success', msg), [addToast]);
  const error = useCallback((msg: string) => addToast('error', msg), [addToast]);
  const warning = useCallback((msg: string) => addToast('warning', msg), [addToast]);
  const info = useCallback((msg: string) => addToast('info', msg), [addToast]);

  return { toasts, addToast, removeToast, success, error, warning, info };
}

// ─── USE INFINITE SCROLL ───────────────────
export function useInfiniteScroll(
  callback: () => void,
  options: { threshold?: number; enabled?: boolean } = {}
) {
  const { threshold = 0.3, enabled = true } = options;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          callback();
        }
      },
      { threshold }
    );

    if (targetRef.current) {
      observerRef.current.observe(targetRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [callback, threshold, enabled]);

  return targetRef;
}

// ─── USE DEBOUNCED VALUE ───────────────────
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

// ─── USE COMMISSION CALCULATOR ─────────────
export function useCommission() {
  const calculateCommission = useCallback((totalRevenue: number) => {
    const platformFee = Math.round(totalRevenue * PLATFORM_COMMISSION_RATE);
    const ownerEarnings = totalRevenue - platformFee;
    return {
      totalRevenue,
      platformFee,
      ownerEarnings,
      commissionRate: PLATFORM_COMMISSION_RATE,
    };
  }, []);

  const formatCurrency = useCallback((amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  }, []);

  return { calculateCommission, formatCurrency };
}

// ─── USE SOCIAL PLATFORMS ──────────────────
export function useSocialPlatforms(userPlan: string) {
  const allowedPlatforms: SocialPlatform[] = useMemo(() => {
    if (userPlan === 'enterprise') return ENTERPRISE_SOCIAL_PLATFORMS;
    if (userPlan === 'pro' || userPlan === 'professional') return PRO_SOCIAL_PLATFORMS;
    return [];
  }, [userPlan]);

  const lockedPlatforms: SocialPlatform[] = useMemo(() => {
    return ENTERPRISE_SOCIAL_PLATFORMS.filter((p) => !allowedPlatforms.includes(p));
  }, [allowedPlatforms]);

  const platformLabels: Record<SocialPlatform, string> = {
    tiktok: 'TikTok',
    facebook: 'Facebook',
    youtube: 'YouTube',
    instagram: 'Instagram',
    x_twitter: 'X (Twitter)',
    linkedin: 'LinkedIn',
    discord: 'Discord',
    slack: 'Slack',
  };

  return { allowedPlatforms, lockedPlatforms, platformLabels };
}

// ─── USE KEYBOARD SHORTCUTS ────────────────
export function useKeyboardShortcut(
  key: string,
  callback: () => void,
  modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean } = {}
) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const { ctrl = false, shift = false, alt = false } = modifiers;
      if (
        e.key === key &&
        e.ctrlKey === ctrl &&
        e.shiftKey === shift &&
        e.altKey === alt
      ) {
        e.preventDefault();
        callback();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [key, callback, modifiers]);
}